import React, { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import _debounce from 'lodash/debounce'
import { validatePak } from '../../../lib/util'
import { connect } from 'react-redux'
import { push } from 'connected-react-router'
import * as notificationActionCreators from '../../../redux/notifications'
import userOperations from '../../../redux/css-admin/userManager/operations'
import * as modalActionsCreators from '../../../redux/modal'
import lmsOperations from '../../../redux/css-admin/lmsManager/operations'
import { getUserLicenses } from '../../../redux/css-admin/licenseManager/operations'
import moment from 'moment'
import _isEqual from 'lodash/isEqual'
import {
  Alert,
  Row,
  Col,
  Badge,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  InputGroup,
  InputGroupAddon,
} from 'reactstrap'
import AutoTable from 'components/AutoTable'
import ErrorBoundary from 'components/ErrorBoundary'
import Loading from 'components/Loading/Loading'
import UserProfile from './UserProfile'
import SitesGrid from './SitesGrid/SitesGrid'

const Tabs = {
  Sites: 1,
  Paks: 2,
  TrainingStatus: 3,
}

export const User = props => {
  const {
    addPakToProductUser,
    clearChildModuleStatus,
    clearLessonProgressReportLink,
    cssLmsTrainingReport,
    cssLmsTrainingTrack,
    deleteUserResults,
    fetchAndHandleArchetypes,
    fetchAndHandleProductUser,
    fetchChildModuleStatus,
    fetchLessonProgressReport,
    fetchTrainingStatusesCss,
    getUserLicenses,
    isDeletingUser,
    isLoading,
    isSaving,
    isSavingTraining,
    match,
    openModal,
    productUser,
    progressReport,
    push,
    setActiveSkuCode,
    userLicenses,
  } = props
  const [activeTab, setActiveTab] = useState(Tabs.Sites)
  const [pakNo, setPakNo] = useState('')
  const [isPakValid, setIsPakValid] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [
    displayHSCompletionReportButton,
    setDisplayHSCompletionReportButton,
  ] = useState(false)
  const [
    displayK8CompletionReportButton,
    setDisplayK8CompletionReportButton,
  ] = useState(false)

  // mount
  useEffect(() => {
    if (!productUser || match.params.id !== productUser.id) {
      fetchAndHandleProductUser(match.params.email, match.params.id)
      fetchAndHandleArchetypes()
      fetchTrainingStatusesCss(match.params.email)
      getUserLicenses(match.params.id)
    }
  }, [])

  useEffect(() => {
    const { licenses } = userLicenses

    if (licenses == null || licenses.length < 1 || productUser === null) {
      setDisplayHSCompletionReportButton(false)
      setDisplayK8CompletionReportButton(false)
      return
    }

    const isHSAdmin = [
      ...licenses.filter(
        l =>
          l.product &&
          l.product.programCodes &&
          l.product.programCodes.includes('HSP'),
      ),
    ].some(
      l =>
        l.admins && l.admins.some(admin => admin.email.toUpperCase() === productUser.email.toUpperCase()),
    )
    setDisplayHSCompletionReportButton(isHSAdmin)

    const isK8Admin = [
      ...licenses.filter(
        l =>
          l.product &&
          l.product.programCodes &&
          l.product.programCodes.some(c => c === ('MSP' || 'K5P')),
      ),
    ].some(
      l =>
        l.admins && l.admins.some(admin => admin.email.toUpperCase() === productUser.email.toUpperCase()),
    )
    setDisplayK8CompletionReportButton(isK8Admin)
  }, [userLicenses, productUser])

  const pakValidation = async pakNo => {
    const pakIsValid = await validatePak(pakNo)
    setIsPakValid(pakIsValid)
    setIsValidating(false)
  }
  const debouncedPakValidation = _debounce(pakValidation, 300)
  useEffect(() => debouncedPakValidation(pakNo), [pakNo])

  const backToSearch = () => push('/css-admin/users')
  useEffect(() => {
    if (
      deleteUserResults &&
      match.params.id === deleteUserResults.user.id.toString()
    ) {
      backToSearch()
    }
  }, [match, deleteUserResults])

  const downloadProgressReport = async url => {
    try {
      const link = document.createElement('a')
      const downloadName = url.split('/').pop()
      link.href = url
      link.download = downloadName
      link.target = '_self'
      link.click()
      clearLessonProgressReportLink()
    } catch (error) {
      console.error(error)
    }
  }
  useEffect(() => {
    if (!progressReport.isLoading && progressReport.data) {
      downloadProgressReport(progressReport.data)
    }
  }, [progressReport])

  useEffect(() => () => clearChildModuleStatus(), [])

  const pakColumns = [
    { title: 'Pak', fieldValue: 'pakNumber' },
    { title: 'Grade', fieldValue: 'grade' },
    {
      title: 'Registered',
      fieldValue: pak => moment(pak.registrationDate).format('MM-DD-YYYY'),
    },
  ]

  const trainingStatusColumns = [
    { title: 'Name', fieldValue: 'type' },
    {
      title: 'Training Path',
      fieldValue: 'current_track_title',
      headerStyle: { width: '2rem' },
      cellClassName: 'text-left',
    },
    {
      title: 'Status',
      fieldValue: row => {
        if (hasTrainingAccess(productUser.paks, row.skuCode)) {
          return row.status
        } else {
          return ''
        }
      },
      headerStyle: { width: '4rem' },
      cellClassName: 'text-left',
    },
    {
      title: 'Certificate Date',
      fieldValue: row => {
        if (row.doneAt !== null) return moment(row.doneAt).format('MM-DD-YYYY')
      },
      headerStyle: { width: '8rem' },
    },
    {
      title: 'Actions',
      fieldValue: row => {
        return (
          <Fragment>
            {!row.current_track_title && (
              <Button
                color="light"
                disabled={isSavingTraining}
                id={row.skuCode.toLowerCase()}
                onClick={() =>
                  handleEditTrainingModel(row.skuCode.toLowerCase())
                }
                role="button"
                size="sm"
              >
                <i className="fas fa-plus text-success" />
                &nbsp;Add
              </Button>
            )}
            {row.current_track_title && (
              <Button
                color="light"
                disabled={isSavingTraining}
                id={row.skuCode.toLowerCase()}
                onClick={() =>
                  handleEditTrainingModel(row.skuCode.toLowerCase())
                }
                role="button"
                size="sm"
              >
                <i className="fas fa-pencil-alt" />
                &nbsp;Edit
              </Button>
            )}
          </Fragment>
        )
      },
      headerStyle: { width: '8rem' },
      cellClassName: 'text-left',
      sort: false,
      filter: false,
    },
  ]

  const addPak = async () => {
    const success = await addPakToProductUser(pakNo)
    if (success) {
      setPakNo('')
      setIsPakValid(false)
      setIsValidating(false)
    }
  }

  const handlePakChange = event => {
    setPakNo(event.target.value)
    setIsPakValid(false)
    setIsValidating(true)
  }

  const hasTrainingAccess = (paks, program) => {
    if (!program) return false
    if (userLicenses.isLoading || !userLicenses.licenses) {
      return false
    }

    const hasTrainingPAK = paks
      .map(pak => pak.pakNumber.substr(0, 3))
      .includes(program.toUpperCase())

    const hasTrainingLicense = userLicenses.licenses
      .reduce((result, lic) => {
        if (lic && lic.product && lic.product.skuCode) {
          result.push(lic.product.skuCode)
        }
        return result
      }, [])
      .includes(program.toUpperCase())

    const hasTraining = hasTrainingPAK || hasTrainingLicense

    return hasTraining
  }

  const handleEditTrainingModel = async skuCode => {
    setActiveSkuCode(skuCode)
    // If user has a current training path call the get route
    const selectedTraining = cssLmsTrainingTrack.find(
      training => training.program === skuCode,
    )
    if (selectedTraining.users_current_track) {
      await fetchChildModuleStatus(
        match.params.email,
        selectedTraining.users_current_track.id,
        skuCode,
      )
    }
    openModal('editTrainingModal')
  }

  const handleDownloadLessonProgressReport = async programCode => {
    if (!productUser || !productUser.email) return
    fetchLessonProgressReport(productUser.email, programCode)
  }

  const trainingTrackCount = () => {
    let count = 0
    cssLmsTrainingTrack.map(x => {
      if (
        x.users_current_track ||
        hasTrainingAccess(productUser.paks, x.program)
      ) {
        if (x.program === 'bpu') count += 1
        if (x.program === 'cpu') count += 1
      }
    })
    return count
  }

  const userTrainingTracks = () => {
    const rows = cssLmsTrainingReport.filter(training =>
      hasTrainingAccess(productUser.paks, training.skuCode),
    )

    return rows
  }

  if (!productUser) return null
  return (
    <ErrorBoundary>
      <Loading isLoading={isSaving} />
      <p>
        <Button
          color="primary"
          data-testid="back-to-search-data-test-id"
          onClick={() => backToSearch(false)}
          outline
          role="button"
        >
          <i className="fas fa-chevron-left" />
          &nbsp; Back to Search
        </Button>
      </p>
      <Loading
        isLoading={isLoading || isDeletingUser}
        message={isDeletingUser ? 'Deleting account...' : 'Loading...'}
      />
      <Row style={{ backgroundColor: '#002855' }}>
        <Col>
          <h2
            className="mt-2 text-light"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              User&nbsp;
              <Badge color="light" data-testid="user-id-data-test-id">
                {productUser.id}
              </Badge>
            </div>
            <Button
              color="light"
              onClick={() => openModal('editProductUserModal')}
              role="button"
              style={{ borderRadius: '2px' }}
            >
              <i className="fas fa-pencil-alt" />
              &nbsp;Edit
            </Button>
          </h2>
        </Col>
      </Row>
      <Row>
        <UserProfile
          displayHSCompletionReportButton={displayHSCompletionReportButton}
          displayK8CompletionReportButton={displayK8CompletionReportButton}
          getLessonProgressReport={handleDownloadLessonProgressReport}
          productUser={productUser}
        />
      </Row>
      <Nav justified tabs>
        <NavItem>
          <NavLink
            active={activeTab === Tabs.Sites}
            data-testid="licenses-tab-data-test-id"
            id="tab-license"
            onClick={() => setActiveTab(Tabs.Sites)}
            onKeyPress={() => setActiveTab(Tabs.Sites)}
            tabIndex={0}
          >
            Sites&nbsp;
            <Badge color="info" data-testid="licenses-badge-data-test-id">
              {productUser.sites.length}
            </Badge>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            active={activeTab === Tabs.Paks}
            data-testid="paks-tab-data-test-id"
            id="tab-paks"
            onClick={() => setActiveTab(Tabs.Paks)}
            onKeyPress={() => setActiveTab(Tabs.Paks)}
            tabIndex={0}
          >
            Paks&nbsp;
            <Badge color="info" data-testid="paks-badge-data-test-id">
              {productUser.paks.length}
            </Badge>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            active={activeTab === Tabs.TrainingStatus}
            data-testid="training-tab-data-test-id"
            id="tab-training"
            onClick={() => setActiveTab(Tabs.TrainingStatus)}
            onKeyPress={() => setActiveTab(Tabs.TrainingStatus)}
            tabIndex={0}
          >
            Training&nbsp;
            <Badge color="info" data-testid="training-badge-data-test-id">
              {trainingTrackCount()}
            </Badge>
          </NavLink>
        </NavItem>
      </Nav>
      <br />
      <TabContent activeTab={activeTab}>
        <TabPane tabId={Tabs.Sites}>
          <SitesGrid licenses={productUser.sites} />
        </TabPane>
        <TabPane tabId={Tabs.Paks}>
          <Row className="justify-content-between mb-2">
            <Col className="d-flex align-items-baseline" sm={8}>
              <h4>Paks</h4>
            </Col>
            <Col className="text-right" sm={4}>
              <InputGroup className="justify-content-end">
                <Input
                  className="border-right-0"
                  maxLength="12"
                  onChange={handlePakChange}
                  type="text"
                  value={pakNo}
                />
                <InputGroupAddon addonType="append">
                  <div
                    className="border-top border-bottom d-flex align-items-center justify-content-center"
                    style={{ width: '25px' }}
                  >
                    {isValidating ? (
                      <i className="fa fa-spinner fa-spin" />
                    ) : isPakValid ? (
                      <i className="fa fa-check" style={{ color: 'green' }} />
                    ) : pakNo.length > 0 ? (
                      <i className="fa fa-times" style={{ color: 'red' }} />
                    ) : null}
                  </div>
                  <Button
                    color="primary"
                    disabled={pakNo === '' || !isPakValid || isSavingTraining}
                    onClick={addPak}
                    role="button"
                    size="sm"
                  >
                    {isSavingTraining ? (
                      <Fragment>
                        <i className="fa fa-spinner fa-spin" />
                        &nbsp;Saving...
                      </Fragment>
                    ) : (
                      <Fragment>
                        <i className="fa fa-user-plus" />
                        &nbsp;Add Pak
                      </Fragment>
                    )}
                  </Button>
                </InputGroupAddon>
              </InputGroup>
            </Col>
          </Row>
          <AutoTable
            borderless
            columns={pakColumns}
            filter
            headThemeLight
            rows={productUser.paks}
            sort
          />
        </TabPane>
        <TabPane tabId={Tabs.TrainingStatus}>
          {userLicenses.isLoading && (
            <Loading
              isLoading={userLicenses.isLoading}
              message="Loading Training"
            />
          )}
          {trainingTrackCount() === 0 && (
            <Alert color="warning">No training licenses found</Alert>
          )}
          {trainingTrackCount() > 0 && (
            <Fragment>
              <Row className="mt-1 mb-1">
                <Col className="d-flex align-items-baseline" sm={10}>
                  <h4>Training Status</h4>
                </Col>
              </Row>
              <AutoTable
                borderless
                columns={trainingStatusColumns}
                headThemeLight
                rows={userTrainingTracks() || []}
              />
            </Fragment>
          )}
        </TabPane>
      </TabContent>
    </ErrorBoundary>
  )
}

User.propTypes = {
  addPakToProductUser: PropTypes.func.isRequired,
  clearChildModuleStatus: PropTypes.func.isRequired,
  cssLmsTrainingReport: PropTypes.array,
  deleteUserResults: PropTypes.shape({
    user: PropTypes.shape({
      id: PropTypes.number,
    }),
  }),
  fetchAndHandleProductUser: PropTypes.func.isRequired,
  fetchLessonProgressReport: PropTypes.func.isRequired,
  fetchTrainingStatusesCss: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSavingTraining: PropTypes.bool.isRequired,
  lmsTrainingStatus: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  lmsTrainingStatusError: PropTypes.object,
  match: PropTypes.object,
  openModal: PropTypes.func,
  productUser: PropTypes.object,
  push: PropTypes.func,
  removePakFromProductUser: PropTypes.func.isRequired,
}

const mapStateToProps = ({
  userManager,
  licenseManager,
  refData,
  lmsManager,
  deleteUserManager,
}) => {
  return {
    cssLmsTrainingReport: lmsManager.training_report,
    cssLmsTrainingTrack: lmsManager.training_track,
    isSavingTraining: lmsManager.isSavingTraining,
    isDeletingUser: deleteUserManager.isDeletingUser,
    deleteUserResults: deleteUserManager.deleteUserResults,
    userLicenses: {
      isLoading: licenseManager.isLoading,
      licenses: licenseManager.userLicenses,
      error: licenseManager.error,
    },
    ...userManager,
    ...refData,
  }
}

const mapDispatchToProps = {
  ...userOperations,
  ...notificationActionCreators,
  ...modalActionsCreators,
  ...lmsOperations,
  getUserLicenses,
  push,
}

export default connect(mapStateToProps, mapDispatchToProps)(User)



// WEBPACK FOOTER //
// ./src/containers/css-admin/users/User.js